import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  Save,
  Mail,
  Send,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Zap,
  TestTube2,
} from "lucide-react";

const PROVIDERS = ["SMTP", "Hostinger", "Gmail", "SendGrid", "Resend", "Mailgun", "SES"];

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
    {children}
    {hint ? <div className="text-[11px] text-gray-500 mt-1">{hint}</div> : null}
  </div>
);

export default function EmailGateway() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/email");
        setForm(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load email gateway");
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyResendPreset = () => {
    setForm((f) => ({
      ...f,
      provider: "Resend",
      smtp_host: "",
      smtp_port: 587,
      username: "",
      use_ssl: false,
      from_email: f.from_email || "onboarding@resend.dev",
      from_name: f.from_name || "Maxlive88",
    }));
    toast.message("Resend template loaded. Paste your API key into 'Password / API Key' below.");
  };

  const applyHostingerPreset = () => {
    setForm((f) => ({
      ...f,
      provider: "Hostinger",
      smtp_host: "smtp.hostinger.com",
      smtp_port: 465,
      use_ssl: true,
      // username + password are user-specific — leave them as-is
      from_email: f.from_email || f.username || "",
      from_name: f.from_name || "Maxlive88",
    }));
    toast.message("Hostinger SMTP preset loaded (smtp.hostinger.com · 465 · SSL). Fill username = your full email, password = email account password.");
  };

  const applyGmailPreset = () => {
    setForm((f) => ({
      ...f,
      provider: "Gmail",
      smtp_host: "smtp.gmail.com",
      smtp_port: 587,
      use_ssl: false,
      from_email: f.from_email || f.username || "",
      from_name: f.from_name || "Maxlive88",
    }));
    toast.message("Gmail SMTP preset loaded (smtp.gmail.com · 587 · STARTTLS). Use a Google App Password as the password.");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/email", form);
      setForm(data);
      toast.success("Email gateway saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const to = (testTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid recipient email to test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/email/test", { to });
      setTestResult({ ok: true, id: data.id, to: data.to });
      toast.success(`Test email sent to ${data.to} ✅`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Test failed";
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  const isResend = (form.provider || "").toLowerCase() === "resend";
  const isSandboxFrom = (form.from_email || "").toLowerCase().endsWith("@resend.dev");
  const hasKey = !!(form.password || "").trim();

  return (
    <div data-testid="email-gateway-page">
      <PageHeader title="Email Gateway" subtitle="Configure the provider used for transactional emails (password reset, KYC, receipts)" />

      {/* Quick-start cards */}
      <div className="max-w-3xl mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Hostinger preset */}
        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-sm p-4 flex items-start gap-3" data-testid="email-hostinger-quickstart">
          <div className="h-10 w-10 rounded-sm bg-violet-500/15 text-violet-700 inline-flex items-center justify-center shrink-0">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Hostinger Business Email</div>
            <div className="text-[12px] text-gray-600 mt-1 leading-relaxed">
              SMTP via <code>smtp.hostinger.com:465 (SSL)</code>. Use your full Hostinger email as username and the email account password.
            </div>
            <button
              type="button"
              onClick={applyHostingerPreset}
              className="mt-2 h-8 px-3 text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white rounded-sm inline-flex items-center gap-1.5"
              data-testid="email-apply-hostinger"
            >
              <Zap size={12} /> Apply Hostinger preset
            </button>
          </div>
        </div>

        {/* Resend preset */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3" data-testid="email-resend-quickstart">
          <div className="h-10 w-10 rounded-sm bg-amber-500/15 text-amber-700 inline-flex items-center justify-center shrink-0">
            <Zap size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Resend (API)</div>
            <div className="text-[12px] text-gray-600 mt-1 leading-relaxed">
              Free 3,000/mo · zero SMTP. Sign up at{" "}
              <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-amber-700 hover:underline font-semibold inline-flex items-center gap-0.5">
                resend.com <ExternalLink size={11} />
              </a>{" "}
              → API Keys (key starts with <code>re_</code>).
            </div>
            <button
              type="button"
              onClick={applyResendPreset}
              className="mt-2 h-8 px-3 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-sm inline-flex items-center gap-1.5"
              data-testid="email-apply-resend"
            >
              <Zap size={12} /> Apply Resend template
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl bg-white border border-gray-200 rounded-sm p-6 space-y-5" data-testid="email-form">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="h-10 w-10 bg-amber-50 text-amber-600 border border-amber-200 rounded-sm flex items-center justify-center">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">Email Provider</div>
            <div className="text-xs text-gray-500">Used for password reset, KYC, promotional campaigns and receipts.</div>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
                data-testid="email-enabled"
              />
              <span className="text-xs font-semibold uppercase tracking-wider">{form.enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Provider">
            <select
              value={form.provider || "SMTP"}
              onChange={(e) => set("provider", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="email-provider"
            >
              {PROVIDERS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>

          {/* SMTP-only fields: hide for API-based providers like Resend */}
          {!isResend ? (
            <>
              <Field label="SMTP Host">
                <input
                  value={form.smtp_host || ""}
                  onChange={(e) => set("smtp_host", e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                  data-testid="email-host"
                />
              </Field>
              <Field label="SMTP Port" hint="465 = SSL · 587 = STARTTLS">
                <input
                  type="number"
                  value={form.smtp_port || 587}
                  onChange={(e) => {
                    const port = parseInt(e.target.value) || 587;
                    set("smtp_port", port);
                    // auto-adjust SSL toggle on common ports
                    if (port === 465) set("use_ssl", true);
                    if (port === 587 || port === 25 || port === 2525) set("use_ssl", false);
                  }}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                />
              </Field>
              <Field label="Username" hint="For Hostinger: your full email address">
                <input
                  value={form.username || ""}
                  onChange={(e) => set("username", e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                  data-testid="email-username"
                  placeholder="noreply@yourdomain.com"
                  autoComplete="off"
                />
              </Field>
              <Field label="Encryption">
                <div className="h-10 flex items-center gap-3 px-3 border border-gray-200 rounded-sm bg-gray-50">
                  <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="email-ssl"
                      checked={!!form.use_ssl}
                      onChange={() => set("use_ssl", true)}
                      data-testid="email-ssl-on"
                    />
                    <span className="font-medium">SSL</span>
                    <span className="text-[11px] text-gray-500">(465)</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="email-ssl"
                      checked={!form.use_ssl}
                      onChange={() => set("use_ssl", false)}
                      data-testid="email-ssl-off"
                    />
                    <span className="font-medium">STARTTLS</span>
                    <span className="text-[11px] text-gray-500">(587)</span>
                  </label>
                </div>
              </Field>
            </>
          ) : (
            <div className="md:col-span-1 flex items-center gap-2 p-3 rounded-sm border border-dashed border-gray-200 bg-gray-50 text-xs text-gray-600">
              <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
              <span>Resend uses REST API — no SMTP host/port/username needed. Just the API key and From address.</span>
            </div>
          )}

          <div className="md:col-span-2">
            <Field
              label={isResend ? "Resend API Key" : "Password / API Key"}
              hint={isResend ? "Key starts with re_ · leave existing key by re-saving only after you rotate it." : undefined}
            >
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={form.password || ""}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder={isResend ? "re_XXXXXXXXXXXXXXXXXXXXXXXX" : ""}
                  className="w-full h-10 pl-3 pr-10 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-amber-500"
                  data-testid="email-password"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
          </div>

          <Field label="From Email" hint={isResend ? "Use onboarding@resend.dev for testing (sandbox), or your verified domain address." : undefined}>
            <input
              type="email"
              value={form.from_email || ""}
              onChange={(e) => set("from_email", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="email-from"
            />
          </Field>
          <Field label="From Name">
            <input
              value={form.from_name || ""}
              onChange={(e) => set("from_name", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="email-from-name"
            />
          </Field>
        </div>

        {isResend && isSandboxFrom && (
          <div className="flex items-start gap-2 p-3 rounded-sm border border-amber-200 bg-amber-50 text-[12px] text-amber-900" data-testid="email-sandbox-warning">
            <AlertCircle size={14} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <b>Sandbox mode:</b> while using <code>onboarding@resend.dev</code> as the From address, Resend only delivers to the email registered on your Resend account. To send to any recipient (password reset, KYC, receipts), verify a domain at{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-amber-800 underline font-semibold">resend.com/domains</a>{" "}
              and switch the From email to something like <code>noreply@maxlive88.com</code>.
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="email-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Email Gateway"}
          </button>
        </div>
      </form>

      {/* Test sender */}
      <div className="max-w-3xl mt-6 bg-white border border-gray-200 rounded-sm p-6" data-testid="email-test-card">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="h-10 w-10 bg-sky-50 text-sky-600 border border-sky-200 rounded-sm flex items-center justify-center">
            <TestTube2 size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Send a test email</div>
            <div className="text-xs text-gray-500">Verifies API key + From address + enabled flag end-to-end.</div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-3 mt-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Recipient email</label>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="your-inbox@example.com"
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-sky-500"
              data-testid="email-test-to"
              disabled={!form.enabled || !hasKey}
            />
          </div>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing || !form.enabled || !hasKey}
            title={!form.enabled ? "Enable + save the gateway first" : !hasKey ? "Set the API key first" : ""}
            className="h-10 px-5 bg-sky-500 hover:bg-sky-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="email-test-send"
          >
            <Send size={14} /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
        {testResult && (
          <div
            className={`mt-4 flex items-start gap-2 p-3 rounded-sm text-xs border ${
              testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="email-test-result"
          >
            {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5" /> : <AlertCircle size={14} className="mt-0.5" />}
            <div>
              {testResult.ok ? (
                <>
                  <div className="font-semibold">Delivered ✓</div>
                  <div className="opacity-80">Sent to <b>{testResult.to}</b>{testResult.id ? <> · id: <span className="font-mono">{testResult.id}</span></> : null}</div>
                </>
              ) : (
                <>
                  <div className="font-semibold">Failed</div>
                  <div className="font-mono opacity-80 break-all">{testResult.error}</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
